import type { OperationsEnvironmentSnapshot } from '@tcvs/shared';
import type { EChartsCoreOption } from 'echarts/core';

const TEXT = '#aeb6d9';
const GRID = 'rgba(101, 216, 255, 0.13)';
const COLORS = ['#f019b7', '#65d8ff', '#55efad'];

function dateLabel(value: string | number): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${String(value)}T00:00:00.000Z`));
}

function usd(value: string | number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(Number(value));
}

export function operationsCostChartOption(
  environments: readonly OperationsEnvironmentSnapshot[],
  wallboard: boolean,
): EChartsCoreOption {
  const dates = [
    ...new Set(
      environments.flatMap((environment) => environment.cost?.daily.map((p) => p.date) ?? []),
    ),
  ].sort();
  if (dates.length === 0) {
    const currentPeriod = environments.find((environment) => environment.cost !== undefined)?.cost;
    if (currentPeriod !== undefined) dates.push(currentPeriod.periodStart);
  }
  return {
    animation: !wallboard,
    animationDuration: wallboard ? 0 : 450,
    animationDurationUpdate: wallboard ? 0 : 300,
    aria: { enabled: true },
    grid: { left: wallboard ? 74 : 64, right: 20, top: 40, bottom: 42 },
    legend: {
      top: 0,
      right: 8,
      textStyle: { color: TEXT },
      data: environments.map((environment) => environment.stage.toUpperCase()),
    },
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(5, 9, 32, 0.96)',
      borderColor: 'rgba(101, 216, 255, 0.45)',
      textStyle: { color: '#fff' },
      valueFormatter: usd,
    },
    xAxis: {
      type: 'category',
      data: dates,
      axisLabel: { color: TEXT, formatter: dateLabel },
      axisLine: { lineStyle: { color: GRID } },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      axisLabel: { color: TEXT, formatter: usd },
      splitLine: { lineStyle: { color: GRID } },
    },
    series: environments.map((environment, index) => {
      const byDate = new Map(
        environment.cost?.daily.map((point) => [point.date, point.netUsd]) ?? [],
      );
      return {
        name: environment.stage.toUpperCase(),
        type: 'line',
        smooth: true,
        showSymbol: dates.length < 12,
        lineStyle: { color: COLORS[index % COLORS.length], width: 3 },
        itemStyle: { color: COLORS[index % COLORS.length] },
        data: dates.map(
          (date) =>
            byDate.get(date) ??
            (date === environment.cost?.periodStart ? environment.cost.netUsd : null),
        ),
      };
    }),
  };
}
